// frontend/src/api/client.js
import axios from "axios";

/**
 * Window event dispatched when the API returns HTTP 402 (plan limit exceeded).
 * The interceptor below fires it with `{ feature, current, limit, upgradeUrl }`
 * so a root-level React listener (the UpgradeModal host in App.jsx) can show
 * the upgrade prompt from anywhere, without coupling this non-React module to
 * the component tree.
 */
export const PLAN_LIMIT_EVENT = "qrflow:plan-limit-exceeded";

/**
 * Axios instance pre-configured for the QRFlow API.
 *
 * baseURL defaults to "" (RELATIVE / same-origin). This is deliberate and
 * important: the SPA must call the API on its OWN origin so the browser treats
 * the refresh + csrf cookies as first-party and actually sends them. The dev
 * Vite server and the prod nginx both proxy ``/api`` → backend, so a relative
 * ``/api/...`` request reaches the backend without ever crossing an origin.
 *
 * A cross-origin baseURL (e.g. ``http://localhost:8000`` while the page is on
 * ``localhost:3000``) breaks the silent-refresh flow: Chrome drops the cookies
 * across the port boundary, so ``POST /api/auth/refresh`` arrives with no
 * cookie and 401s, logging the user out on every reload. Only set VITE_API_URL
 * if the API genuinely lives on a separate origin AND you've configured
 * SameSite=None;Secure + CORS for it.
 *
 * - withCredentials so the HttpOnly refresh cookie + csrf_token cookie are sent
 * - Request interceptor attaches the Authorization Bearer token and the
 *   X-CSRF-Token header (double-submit CSRF protection)
 * - Response interceptor redirects to /login on 401
 */
const client = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "",
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Double-submit CSRF token.
 *
 * Held in module memory (NOT localStorage) so it is never readable by a
 * persisted-XSS payload across reloads. The login/refresh response body carries
 * the token; we store it here and echo it as the X-CSRF-Token header, which the
 * backend compares against the csrf_token cookie.
 */
let csrfToken = null;

/** Store the CSRF token from a login/refresh response in memory. */
export function setCsrfToken(token) {
  csrfToken = token || null;
}

/** Read the in-memory CSRF token (mainly for tests/diagnostics). */
export function getCsrfToken() {
  return csrfToken;
}

/**
 * Access token (JWT).
 *
 * Held in module memory — NOT localStorage — so a persisted-XSS payload can't
 * read it and it never outlives the tab. It is lost on reload by design; the
 * session is recovered from the HttpOnly refresh cookie via /api/auth/refresh.
 */
let accessToken = null;

/** Store the access token in memory (called by login/refresh). */
export function setAccessToken(token) {
  accessToken = token || null;
}

/** Read the in-memory access token. */
export function getAccessToken() {
  return accessToken;
}

/**
 * Attach the Bearer token and the X-CSRF-Token header on every request.
 *
 * Org scoping needs no manual header: the active organisation rides inside the
 * JWT as the `org_id` claim, so switching orgs (which swaps the access token)
 * automatically re-scopes every subsequent request.
 */
client.interceptors.request.use(
  (config) => {
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    if (csrfToken) {
      config.headers["X-CSRF-Token"] = csrfToken;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Auth endpoints whose own 401s must NOT trigger a refresh-retry: a 401 here
// means authentication genuinely failed (bad creds, expired/!=valid refresh
// cookie, already-revoked session), so refreshing would loop or be pointless.
const NON_REFRESHABLE_PATHS = [
  "/api/auth/login",
  "/api/auth/register",
  "/api/auth/refresh",
  "/api/auth/logout",
];

function isNonRefreshable(url) {
  return !!url && NON_REFRESHABLE_PATHS.some((p) => url.includes(p));
}

// Single-flight guard: concurrent 401s share ONE refresh call instead of each
// firing its own (which would rotate the refresh token repeatedly and fail).
let refreshPromise = null;

/**
 * On 401, transparently refresh the access token once and retry the request.
 * If the refresh also fails, clear credentials and redirect to /login.
 */
client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    // 402 Payment Required → plan limit hit. Broadcast the details so the
    // root-level UpgradeModal host can prompt the user. We still reject so the
    // calling code can stop its own flow (e.g. not close a creation form).
    if (status === 402) {
      const data = error.response?.data || {};
      if (
        typeof window !== "undefined" &&
        data.error === "plan_limit_exceeded"
      ) {
        window.dispatchEvent(
          new CustomEvent(PLAN_LIMIT_EVENT, {
            detail: {
              feature: data.feature,
              current: data.current,
              limit: data.limit,
              upgradeUrl: data.upgrade_url || "/billing",
            },
          })
        );
      }
      return Promise.reject(error);
    }

    if (
      status === 401 &&
      original &&
      !original._retry &&
      !isNonRefreshable(original.url)
    ) {
      original._retry = true; // ensure we only refresh-retry once per request
      try {
        if (!refreshPromise) {
          // Lazy import avoids a static client<->auth import cycle.
          const { refreshToken } = await import("./auth");
          refreshPromise = refreshToken().finally(() => {
            refreshPromise = null;
          });
        }
        const newToken = await refreshPromise;
        // Retry with the rotated token (the request interceptor also re-reads
        // it from storage, but set it explicitly to be safe).
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return client(original);
      } catch (refreshError) {
        // Refresh failed → the session is over.
        accessToken = null;
        csrfToken = null;
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default client;
